/**
 * RTWO Indexer Engine — Address Indexing Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type { IndexedAddressId, RTWOIndexedAddress } from '../domain-models';
import type { AddressIndexResult, IAddressIndexingService } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import type { LedgerAdapterRegistry } from '../registry/adapter-registries';
import { IndexerInternalEventTypes } from '../events/indexer-event-bus';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class AddressIndexingService extends BaseIndexerService implements IAddressIndexingService {
  readonly serviceId = INDEXER_SERVICE_IDS.ADDRESS_INDEXING;
  readonly serviceName = 'Address Indexing Service';
  readonly description =
    'Indexes addresses via Ledger adapter. No fake addresses or balances indexed.';
  readonly integrations = ['RPC Platform', 'Ledger Engine', 'Cryptography Engine'];
  readonly requiresRPCSource = true;
  readonly requiresIndexStorage = false;

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry
  ) {
    super(context);
  }

  async indexAddress(address: string): Promise<AddressIndexResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        success: false,
        indexedAddressId: null,
        error:
          'Ledger Engine Waiting for Real Data — Cannot index addresses until Ledger Engine is connected.',
        isPlaceholder: true,
        source: 'Indexer Engine — Ledger Engine Waiting for Real Data',
      };
    }

    const adapterResult = await this.ledgerRegistry.getActiveAdapter().indexAddress(address);

    if (adapterResult?.address) {
      this.repositories.addresses.save(adapterResult.address);
      this.context.eventBus.emit(IndexerInternalEventTypes.ADDRESS_INDEXED, {
        indexedAddressId: adapterResult.address.indexedAddressId,
        address,
      });
    }

    return {
      success: false,
      indexedAddressId: null,
      error:
        'Ledger Engine Waiting for Real Data — Cannot index addresses until Ledger Engine is connected.',
      isPlaceholder: true,
      source: 'Indexer Engine — Ledger Engine Waiting for Real Data',
    };
  }

  getAllIndexedAddresses(): RTWOIndexedAddress[] {
    if (!this.ledgerRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.addresses.findAll();
  }

  getIndexedAddress(indexedAddressId: IndexedAddressId): RTWOIndexedAddress | null {
    if (!this.ledgerRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.addresses.findById(indexedAddressId);
  }

  getIndexedAddressCount(): 0 {
    return 0;
  }
}
